import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOpsAuth } from '@/ops/OpsAuthContext';
import { Loader2, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import '@/ops/ops-theme.css';

export default function OpsLoginPage() {
  const { login, verify2fa, resend2fa, opsUser } = useOpsAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);
  // 2FA step
  const [stage, setStage] = useState('password');   // 'password' | 'code'
  const [challenge, setChallenge] = useState('');
  const [info, setInfo] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => { if (opsUser) navigate('/ops/dashboard', { replace: true }); }, [opsUser, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const r = await login(form.email, form.password);
      if (r.mfaRequired) { setChallenge(r.challenge); setInfo(r.message || ''); setStage('code'); }
    }
    catch (err) { setError(err?.response?.data?.detail || 'Invalid credentials'); }
    finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await verify2fa(challenge, code.trim(), remember); }
    catch (err) { setError(err?.response?.data?.detail || 'Incorrect code'); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    setError('');
    try { const r = await resend2fa(challenge); setInfo(r.message || 'A new code was sent.'); setResent(true); setTimeout(() => setResent(false), 4000); }
    catch (err) { setError(err?.response?.data?.detail || 'Could not resend the code'); }
  };

  return (
    <div className="ops-root min-h-screen flex items-center justify-center px-4">
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div className="text-center mb-7">
          <div className="leading-none mb-2">
            <span style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: 38, color: 'var(--ops-text)' }}>Milk</span>
            <span className="ops-mono" style={{ fontSize: 18, color: 'var(--ops-muted)', marginLeft: 5 }}>/ops</span>
          </div>
          <p className="ops-overline">Operations Console</p>
        </div>

        <div className="ops-card p-7" style={{ boxShadow: '0 24px 60px -20px rgba(0,0,0,0.7)' }}>
          {stage === 'password' ? (
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div className="space-y-1.5">
              <label className="ops-overline">Email</label>
              <input type="email" value={form.email} required autoComplete="username"
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="ops@company.com" data-testid="ops-login-email"
                className="ops-input w-full h-11 px-3.5" style={{ fontSize: 16 }} />
            </div>
            <div className="space-y-1.5">
              <label className="ops-overline">Password</label>
              <div className="relative">
                <input type={showPass ? 'text' : 'password'} value={form.password} required autoComplete="new-password"
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="••••••••" data-testid="ops-login-password"
                  className="ops-input w-full h-11 px-3.5 pr-10" style={{ fontSize: 16 }} />
                <button type="button" onClick={() => setShowPass(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ops-muted)' }}>
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="ops-badge ops-badge-danger w-full justify-center py-2" style={{ borderRadius: 6 }} data-testid="ops-login-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="ops-login-submit"
              className="ops-btn ops-btn-primary w-full h-11">
              {loading ? <><Loader2 size={14} className="animate-spin" />Authenticating…</> : 'Sign in'}
            </button>
          </form>
          ) : (
          <form onSubmit={handleVerify} className="space-y-4" autoComplete="off" data-testid="ops-2fa-form">
            <div className="text-center">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(105,148,198,0.14)' }}>
                <Mail size={18} style={{ color: 'var(--ops-text-2)' }} />
              </div>
              <p className="ops-h2" style={{ color: 'var(--ops-text)' }}>Enter your sign-in code</p>
              <p className="ops-sub mt-1" data-testid="ops-2fa-info">{info || 'We emailed you a 6-digit code.'}</p>
            </div>
            <input inputMode="numeric" pattern="[0-9]*" maxLength={6} value={code} required autoFocus
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="••••••" data-testid="ops-2fa-code"
              className="ops-input w-full h-12 text-center" style={{ fontSize: 26, letterSpacing: 10, fontFamily: 'monospace' }} />
            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--ops-text-2)' }} data-testid="ops-2fa-remember">
              <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
              Trust this device for 12 hours
            </label>

            {error && (
              <div className="ops-badge ops-badge-danger w-full justify-center py-2" style={{ borderRadius: 6 }} data-testid="ops-2fa-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading || code.length < 6} data-testid="ops-2fa-submit"
              className="ops-btn ops-btn-primary w-full h-11">
              {loading ? <><Loader2 size={14} className="animate-spin" />Verifying…</> : 'Verify & sign in'}
            </button>
            <div className="flex items-center justify-between text-xs" style={{ color: 'var(--ops-muted)' }}>
              <button type="button" onClick={() => { setStage('password'); setCode(''); setError(''); }} data-testid="ops-2fa-back">← Back</button>
              <button type="button" onClick={handleResend} data-testid="ops-2fa-resend">{resent ? 'Code sent ✓' : 'Resend code'}</button>
            </div>
          </form>
          )}
        </div>

        <p className="flex items-center justify-center gap-1.5 text-center text-xs mt-5" style={{ color: 'var(--ops-muted)' }}>
          <Lock size={11} /> Access is audited and logged.
        </p>
      </div>
    </div>
  );
}
